import { Inject, Injectable, Injector } from '@angular/core';
import { filterNil } from '../../@core/utils/rxjs.helpers';
import { LunchAppUser, LoginGQL, RefreshAccessTokenGQL, LoginQueryVariables, RefreshAccessTokenQueryVariables } from '@app/@graphql/data';
import { UserRoles } from '@app/@graphql/models';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { delay, map, mapTo, tap } from 'rxjs/operators';
import { AuthState, createAuthState } from '../models';
import { ClaimsService } from './claims.service';
import { TokenService } from './token.service';
import { environment } from '../../../environments'
import moment from 'moment';
import { WindowRef } from '@app/@core';
import { Settings, SETTINGS_TOKEN } from '@app/settings.config';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _state$: BehaviorSubject<AuthState>;
  state$: Observable<AuthState>;

  get state() {
    return this._state$.getValue();
  }

  constructor(
  private injector: Injector,
  @Inject(SETTINGS_TOKEN) private settings: Settings
) {
  const tokenService = this.injector.get(TokenService);
  const claims = this.injector.get(ClaimsService);

  if (tokenService.has() && !tokenService.isAccessTokenExpired()) {
    const accessToken = tokenService.accessToken();
    const refreshToken = tokenService.refreshToken();
    const accessTokenExpirationDate = tokenService.accessTokenExpirationDate();
    const refreshTokenExpirationDate = tokenService.refreshTokenExpirationDate();
    const userId = tokenService.userId();
    const { roles, userName } = claims.getUserData(accessToken);

    this._state$ = new BehaviorSubject<AuthState>(
      createAuthState({
        accessToken,
        refreshToken,
        accessTokenExpirationDate,
        refreshTokenExpirationDate,
        roles,
        userName,
        userId
      })
    );
    this.stopRefreshTokenTimer();
    this.startRefreshTokenTimer();
  } else {
    this.injector.get(TokenService).reset();
    this._state$ = new BehaviorSubject<AuthState>(createAuthState());
  }

  this.state$ = this._state$.asObservable();
  }
  
  private get tokenService(): TokenService {
  return this.injector.get(TokenService);
}

  private get claims(): ClaimsService {
    return this.injector.get(ClaimsService);
  }

  private get loginGQL(): LoginGQL {
    return this.injector.get(LoginGQL);
  }

  private get refreshAccessTokenGQL(): RefreshAccessTokenGQL {
    return this.injector.get(RefreshAccessTokenGQL);
  }

  getAccessToken(): string {
    return this.state.accessToken;
  }

  getAuthorizationHeader(): { [name: string]: string | string[] } {
    const token = this.tokenService.accessToken();
    return {
      Authorization: token ? `Bearer ${token}` : ''
    };
  }

  getIsLoggedIn(): boolean {
    return this.tokenService.has() && !this.tokenService.isRefreshTokenExpired();
  }

  getIsRestaurantAdmin(): boolean {
    return this.state.roles.includes(UserRoles.RestaurantAdmin);
  }

  getIsCompanyAdmin(): boolean {
    return this.state.roles.includes(UserRoles.CompanyAdmin);
  }

  getIsAdmin(): boolean {
    return this.state.roles.includes(UserRoles.Admin);
  }

  getIsAdminOverride(): boolean {
    const result = this.state.roles.includes(UserRoles.AdminOverride);
    return result;
  }

  getHasSomeRole(roles: UserRoles) {
    return this.state.roles.some(role => roles.includes(role));
  }

  getUserId(): string {
    return this.state.userId;
  }

  getUserName(): string {
    return this.state.userName;
  }

  selectIsLoggedIn(): Observable<boolean> {
    return this.state$.pipe(map(state => !!state.accessToken));
  }

  selectIsRestaurantAdmin(): Observable<boolean> {
    return this.state$.pipe(map(state => state.roles.includes(UserRoles.RestaurantAdmin)));
  }

  selectIsWorkplaceAdmin(): Observable<boolean> {
    return this.state$.pipe(map(state => state.roles.includes(UserRoles.CompanyAdmin)));
  }

  selectIsAdmin(): Observable<boolean> {
    return this.state$.pipe(map(state => state.roles.includes(UserRoles.Admin)));
  }

  selectIsAdminOverriden(): Observable<boolean> {
    return this.state$.pipe(map(state => state.roles.includes(UserRoles.AdminOverride)));
  }

  selectRoles(): Observable<UserRoles[]> {
    return this.state$.pipe(map(state => state.roles));
  }

  selectUserName(): Observable<string> {
    return this.state$.pipe(
      filterNil,
      map(state => state.userName)
    );
  }

  selectUserId(): Observable<string> {
    return this.state$.pipe(
      filterNil,
      map(state => state.userId)
    );
  }

selectLoggedUser(): Observable<LunchAppUser> {
  return this.state$.pipe(
    map(({ userId: id, roles, userName }) => ({
      id,
      roles,
      userName,
      isRegulationsConfirmed: false, // lub true, jeśli masz dane
      regulationsConfirmedDate: null // lub new Date().toISOString() jeśli chcesz placeholder
    }))
  );
}

  refreshToken(): Observable<boolean>
  {
    var args:RefreshAccessTokenQueryVariables = { refreshToken: this.tokenService.refreshToken() };

    return this.refreshAccessTokenGQL.fetch(args).pipe(
      map(result => result.data),
      map(r => r.refreshAccessToken),
      tap(({ id: userId, accessToken, refreshToken, accessTokenExpirationDate, refreshTokenExpirationDate }) => {
        const { roles, userName } = this.claims.getUserData(accessToken);
        this._state$.next(
          createAuthState({
            accessToken,
            refreshToken,
            accessTokenExpirationDate,
            refreshTokenExpirationDate,
            userId,
            roles,
            userName
          })
        );
        this.tokenService.save(accessToken, refreshToken, accessTokenExpirationDate, refreshTokenExpirationDate, userId);
        this.startRefreshTokenTimer();
      }),
      mapTo(true)
    );
  }

  login(loginData: LoginQueryVariables): Observable<boolean> {
      return this.loginGQL.fetch(loginData).pipe(
        map(result => result.data),
        map(r => r.login),
        tap(({ id: userId, accessToken, refreshToken, accessTokenExpirationDate, refreshTokenExpirationDate }) => {
          const { roles, userName } = this.claims.getUserData(accessToken);
          this._state$.next(
            createAuthState({
              accessToken,
              refreshToken,
              accessTokenExpirationDate,
              refreshTokenExpirationDate,
              userId,
              roles,
              userName
            })
          );
          this.tokenService.save(accessToken, refreshToken, accessTokenExpirationDate, refreshTokenExpirationDate, userId);
          this.startRefreshTokenTimer();
        }),
        mapTo(true)
      );
  }

  logout() {
    this.tokenService.reset();
    this.stopRefreshTokenTimer();
    this._state$.next(createAuthState());
  }

  private refreshTokenTimeout: NodeJS.Timer;

  private startRefreshTokenTimer() {
      const expires = moment(this.tokenService.accessTokenExpirationDate()).valueOf();
      const timeout = expires - moment().valueOf() - (60 * 1000);
      this.refreshTokenTimeout = setTimeout(() => this.refreshToken().subscribe(), timeout);
  }

  private stopRefreshTokenTimer() {
      clearTimeout(this.refreshTokenTimeout);
  }
}
